import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { storeDosRecommendations, getStoredDosRecommendations } from "../../../utils/recsCache";
import { requestRecommendations } from "../../../utils/requestRecommendations";
import { getRiskLevelPriorityColors } from "../../../utils/riskLevelUtils";
import { levelDisplayNames } from "../../../utils/displayNameUtils";
import { subcategoryLabelsMap } from "../../../utils/categoryLabels";

const getAttackTypeLabel = (key) => subcategoryLabelsMap[key] || (key && key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())) || key;

const sectionCardShellStyle = {
  background: "linear-gradient(145deg, var(--card-bg), rgba(59,130,246,0.05))",
  borderRadius: "16px",
  border: "1px solid var(--card-border)",
  boxShadow: "0 12px 30px rgba(15,23,42,0.08)",
};

const SectionCardIcon = ({ icon }) => (
  <div
    style={{
      width: "40px",
      height: "40px",
      borderRadius: "12px",
      background: "rgba(59,130,246,0.15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "20px",
      color: "#3b82f6",
      flexShrink: 0,
    }}
  >
    {icon}
  </div>
);

const SectionCard = ({ children, title, icon }) => (
  <div
    style={{
      ...sectionCardShellStyle,
      padding: "24px",
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <SectionCardIcon icon={icon} />
      <h3 style={{ margin: 0, color: "var(--card-text)", fontSize: "18px" }}>{title}</h3>
    </div>
    {children}
  </div>
);

const SectionCardAccordion = ({ children, title, icon, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div style={{ ...sectionCardShellStyle, overflow: "hidden" }}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          padding: "24px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
          <SectionCardIcon icon={icon} />
          <h3 style={{ margin: 0, color: "var(--card-text)", fontSize: "18px" }}>{title}</h3>
        </div>
        <i
          className={`fas fa-chevron-${isOpen ? "up" : "down"}`}
          style={{ color: "#64748b", fontSize: "14px", flexShrink: 0 }}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            padding: "0 24px 24px",
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};


const AgenticAIDosReportTabRecommendations = ({ reportData }) => {
  const { t } = useTranslation("reports_recs", { keyPrefix: "recs.aiDos" });
  const [recommendationDetails, setRecommendationDetails] = useState(() => {

    const reportId = reportData?.id;
    return reportId ? getStoredDosRecommendations(reportId) : null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  // uncomment in prod
  useEffect(() => {
    const reportId = reportData?.id;
    if (!reportId) {
      setRecommendationDetails(null);
      return;
    }

    const stored = getStoredDosRecommendations(reportId);
    if (stored) {
      setRecommendationDetails(stored);
    } else {
      setRecommendationDetails(null);
    }
  }, [reportData?.id]);
  // uncomment in prod

  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get report ID from reportData
      const reportId = reportData?.id;
      if (!reportId) {
        throw new Error(t("reportIdNotFound"));
      }

      const result = await requestRecommendations({
        recommFor: "dos",
        reportId,
        reportData,
        fallbackMessage: t("noRecommendationsAvailable"),
      });
      setRecommendationDetails(result);
      
      if (result && Object.keys(result).length > 0) {
        storeDosRecommendations(reportId, result);
      }
    } catch (err) {
      console.error("Error fetching recommendations:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Show button to load recommendations if not loaded yet
  if (!recommendationDetails && !loading) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(59, 130, 246, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-lightbulb" style={{ fontSize: "28px", color: "#3b82f6" }}></i>
        </div>
      
        {error && (
          <div style={{ 
            fontSize: "14px", 
            color: "#ef4444", 
            marginBottom: "20px",
            padding: "12px 20px",
            background: "rgba(239, 68, 68, 0.1)",
            borderRadius: "8px",
            border: "1px solid rgba(239, 68, 68, 0.2)"
          }}>
            {t("noRecommendationsFoundTitle")}
          </div>
        )}
        <button
          onClick={fetchRecommendations}
          disabled={loading}
          style={{
            padding: "12px 32px",
            background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: loading ? "not-allowed" : "pointer",
            transition: "all 0.3s ease",
            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
            opacity: loading ? 0.6 : 1
          }}
          onMouseOver={(e) => {
            if (!loading) {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 16px rgba(59, 130, 246, 0.4)";
            }
          }}
          onMouseOut={(e) => {
            if (!loading) {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
            }
          }}
        >
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin" style={{ marginRight: "8px" }}></i>
              {t("loading")}
            </>
          ) : (
            <>
              <i className="fas fa-download" style={{ marginRight: "8px" }}></i>
              {t("loadRecommendations")}
            </>
          )}
        </button>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(59, 130, 246, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: "28px", color: "#3b82f6" }}></i>
        </div>
        <div style={{ fontSize: "18px", color: "var(--card-text)", fontWeight: 600 }}>
          {t("loadingRecommendations")}
        </div>
      </div>
    );
  }

  // Show error if recommendations failed to load
  if (error && !recommendationDetails) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(239, 68, 68, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: "28px", color: "#ef4444" }}></i>
        </div>
        <div style={{ fontSize: "18px", color: "var(--card-text)", fontWeight: 600, marginBottom: "20px" }}>
          {t("noRecommendationsFoundTitle")}
        </div>
        <button
          onClick={fetchRecommendations}
          style={{
            padding: "12px 32px",
            background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.3s ease",
            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)"
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-2px)";
            e.target.style.boxShadow = "0 6px 16px rgba(59, 130, 246, 0.4)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
          }}
        >
          <i className="fas fa-redo" style={{ marginRight: "8px" }}></i>
          {t("retry")}
        </button>
      </div>
    );
  }

  // Show empty state if recommendations loaded but empty
  if (recommendationDetails && Object.keys(recommendationDetails).length === 0) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(107, 114, 128, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-compass" style={{ fontSize: "28px", color: "#6b7280" }}></i>
        </div>
        <div style={{ fontSize: "18px", color: "var(--card-text)", fontWeight: 600 }}>
          {t("noRecommendationsAvailable")}
        </div>
      </div>
    );
  }

  const {
    executive_summary,
    security_assessment,
    recommendations,
    external_ddos_solutions,
    test_metrics,
    metadata
  } = recommendationDetails;

  const overallRisk = executive_summary?.overall_risk || security_assessment?.overall_risk || "medium";
  const priorityStyle = getRiskLevelPriorityColors(overallRisk?.toLowerCase() || "medium");
  const tableHeaderStyle = { padding: "14px 16px", textAlign: "left", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--muted-text)", background: "rgba(59, 130, 246, 0.08)", borderBottom: "1px solid var(--card-border)" };

  const summary = executive_summary?.summary || security_assessment?.summary;
  const monitoringCount = recommendations?.monitoring?.length || 0;
  const maintenanceCount = recommendations?.maintenance?.length || 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "10px 0 40px" }}>
      {/* Section 1: Executive Summary */}
      {executive_summary && (
        <SectionCard title={t("executiveSummary")} icon={<i className="fas fa-chart-pie"></i>}>
          <div style={{
            background: priorityStyle.gradient,
            borderRadius: "12px",
            padding: "28px",
            color: "white",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: "14px"
          }}>
            <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", opacity: 0.85 }}>{t("overallRisk")}</div>
            <div style={{ fontSize: "11px", fontWeight: 500, opacity: 0.8, marginTop: "-6px" }}>
              {t("basedOnGeneratedPrompts")}
            </div>
            <div style={{ fontSize: "36px", fontWeight: 700, opacity: 0.95, letterSpacing: "0.08em" }}>
              {overallRisk ? (levelDisplayNames[overallRisk.toLowerCase()] || overallRisk) : t("notAvailable")}
            </div>
            {executive_summary.security_posture && (
              <div style={{ fontSize: "14px", opacity: 0.9, lineHeight: 1.4 }}>
                {executive_summary.security_posture}
              </div>
            )}
          </div>
          {/* Chips: 2 columns */}
          {((summary && Object.keys(summary).length > 0) || executive_summary.vulnerabilities_count != null || executive_summary.strengths_count != null || executive_summary.improvement_areas_count != null || executive_summary.readiness_score != null) && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "20px" }}>
              {/* Column 1: readiness, total_tests, total_passed, total_failed, pass_rate */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
                {executive_summary.readiness_score != null && <span style={{ padding: "8px 14px", borderRadius: "999px", fontSize: "13px", fontWeight: 600, color: "#1e40af", background: "#dbeafe", border: "1px solid #93c5fd" }}>{Number(executive_summary.readiness_score).toFixed(1)}% {t("readinessChip")}</span>}
                {summary?.total_tests != null && <span style={{ padding: "8px 14px", borderRadius: "999px", fontSize: "13px", fontWeight: 600, color: "#1e40af", background: "#dbeafe", border: "1px solid #93c5fd" }}>{summary.total_tests} {t("testsChip")}</span>}
                {summary?.total_passed != null && <span style={{ padding: "8px 14px", borderRadius: "999px", fontSize: "13px", fontWeight: 600, color: "#fff", background: "#16a34a", border: "1px solid #15803d" }}>{summary.total_passed} {t("passedChip")}</span>}
                {summary?.total_failed != null && <span style={{ padding: "8px 14px", borderRadius: "999px", fontSize: "13px", fontWeight: 600, color: "#fff", background: "#dc2626", border: "1px solid #b91c1c" }}>{summary.total_failed} {t("failedChip")}</span>}
                {summary?.pass_rate != null && <span style={{ padding: "8px 14px", borderRadius: "999px", fontSize: "13px", fontWeight: 600, color: "#5b21b6", background: "#ede9fe", border: "1px solid #a78bfa" }}>{Number(summary.pass_rate).toFixed(1)}% {t("passRateChip")}</span>}
              </div>
              {/* Column 2: categories_tested, attack_types_tested, vulnerable_attack_types, vulnerabilities_count, strengths_count, improvement_areas_count */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
                {summary?.categories_tested != null && <span style={{ padding: "8px 14px", borderRadius: "999px", fontSize: "13px", fontWeight: 600, color: "#1e40af", background: "#dbeafe", border: "1px solid #93c5fd" }}>{summary.categories_tested} {t("categoriesChip")}</span>}
                {summary?.attack_types_tested != null && <span style={{ padding: "8px 14px", borderRadius: "999px", fontSize: "13px", fontWeight: 600, color: "#1e40af", background: "#dbeafe", border: "1px solid #93c5fd" }}>{summary.attack_types_tested} {t("attackTypesChip")}</span>}
                {summary?.vulnerable_attack_types != null && <span style={{ padding: "8px 14px", borderRadius: "999px", fontSize: "13px", fontWeight: 600, color: "#fff", background: "#dc2626", border: "1px solid #b91c1c" }}>{summary.vulnerable_attack_types} {t("vulnerableChip")}</span>}
                {executive_summary.vulnerabilities_count != null && <span style={{ padding: "8px 14px", borderRadius: "999px", fontSize: "13px", fontWeight: 600, color: "#fff", background: "#b91c1c", border: "1px solid #991b1b" }}>{executive_summary.vulnerabilities_count} {t("vulnerabilitiesChip")}</span>}
                {executive_summary.strengths_count != null && <span style={{ padding: "8px 14px", borderRadius: "999px", fontSize: "13px", fontWeight: 600, color: "#fff", background: "#16a34a", border: "1px solid #15803d" }}>{executive_summary.strengths_count} {t("strengthsChip")}</span>}
                {executive_summary.improvement_areas_count != null && <span style={{ padding: "8px 14px", borderRadius: "999px", fontSize: "13px", fontWeight: 600, color: "#fff", background: "#d97706", border: "1px solid #b45309" }}>{executive_summary.improvement_areas_count} {t("improvementAreasChip")}</span>}
              </div>
            </div>
          )}
          {(executive_summary.key_findings?.length > 0 || executive_summary.business_impact?.length > 0) && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "20px" }}>
              {executive_summary.key_findings?.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#3b82f6", fontWeight: 600 }}>{t("keyFindings")}</div>
                  {executive_summary.key_findings.map((item, i) => (
                    <div key={i} style={{ padding: "10px 14px", background: "rgba(59, 130, 246, 0.06)", borderRadius: "8px", fontSize: "14px", color: "var(--card-text)", lineHeight: 1.5 }}>{item}</div>
                  ))}
                </div>
              )}
              {executive_summary.business_impact?.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#ef4444", fontWeight: 600 }}>{t("businessImpact")}</div>
                  {executive_summary.business_impact.map((item, i) => (
                    <div key={i} style={{ padding: "10px 14px", background: "rgba(239, 68, 68, 0.06)", borderRadius: "8px", fontSize: "14px", color: "var(--card-text)", lineHeight: 1.5 }}>{item}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </SectionCard>
      )}

      {/* Section 2: Security Assessment */}
      {security_assessment && (
        <SectionCardAccordion title={t("securityAssessment")} icon={<i className="fas fa-shield-alt"></i>}>
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {security_assessment.vulnerabilities?.length > 0 && (
              <div>
                <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted-text)", marginBottom: "12px", fontWeight: 600 }}>{t("vulnerabilities")}</div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0", fontSize: "14px" }}>
                    <thead>
                      <tr>
                        <th style={{ ...tableHeaderStyle, borderTopLeftRadius: "10px" }}>{t("attackType")}</th>
                        <th style={tableHeaderStyle}>{t("risk")}</th>
                        <th style={tableHeaderStyle}>{t("errorRate")}</th>
                        <th style={tableHeaderStyle}>{t("failed")}</th>
                        <th style={tableHeaderStyle}>{t("totalRequests")}</th>
                        <th style={tableHeaderStyle}>{t("details")}</th>
                        <th style={{ ...tableHeaderStyle, borderTopRightRadius: "10px" }}>{t("impact")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {security_assessment.vulnerabilities.map((v, i) => {
                        const isLast = i === security_assessment.vulnerabilities.length - 1;
                        const riskKey = v.risk_level?.toLowerCase();
                        const isKnownRisk = riskKey && levelDisplayNames[riskKey];
                        const riskStyle = isKnownRisk ? getRiskLevelPriorityColors(riskKey) : null;
                        const baseTd = { padding: "14px 16px", borderBottom: isLast ? "none" : "1px solid var(--card-border)", verticalAlign: "middle" };
                        return (
                          <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : "rgba(59, 130, 246, 0.03)" }}>
                            <td style={{ ...baseTd, fontWeight: 600, color: "var(--card-text)", ...(isLast ? { borderBottomLeftRadius: "10px" } : {}) }}>
                              <div>{getAttackTypeLabel(v.attack_type)}</div>
                              {v.test_id && <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "2px", fontWeight: 400 }}>{v.test_id}</div>}
                            </td>
                            <td style={baseTd}>
                              <span style={{
                                padding: "4px 10px",
                                borderRadius: "999px",
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "white",
                                background: riskStyle ? riskStyle.gradient : "#6b7280"
                              }}>
                                {riskStyle ? (levelDisplayNames[riskKey] || v.risk_level) : t("unknown")}
                              </span>
                            </td>
                            <td style={baseTd}>{v.error_rate != null ? `${(v.error_rate * 100).toFixed(1)}%` : t("notAvailable")}</td>
                            <td style={baseTd}>{v.failed_count != null ? v.failed_count : t("notAvailable")}</td>
                            <td style={baseTd}>{v.total_requests != null ? v.total_requests : t("notAvailable")}</td>
                            <td style={{ ...baseTd, color: "var(--card-text)", lineHeight: 1.5 }}>{v.details || t("notAvailable")}</td>
                            <td style={{ ...baseTd, color: "var(--card-text)", lineHeight: 1.5, ...(isLast ? { borderBottomRightRadius: "10px" } : {}) }}>{v.impact || v.warning || t("notAvailable")}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              {security_assessment.strengths?.length > 0 && (
                <div style={{ background: "rgba(34, 197, 94, 0.06)", borderRadius: "12px", padding: "16px", border: "1px solid rgba(34, 197, 94, 0.15)" }}>
                  <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#22c55e", marginBottom: "12px", fontWeight: 600 }}>{t("strengths")}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "8px 16px" }}>
                    {security_assessment.strengths.map((s, i) => (
                      <div key={i} style={{ fontSize: "14px", color: "var(--card-text)", lineHeight: 1.5 }}>{s}</div>
                    ))}
                  </div>
                </div>
              )}
              {security_assessment.improvement_areas?.length > 0 && (
                <div>
                  <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#ef4444", marginBottom: "12px", fontWeight: 600 }}>{t("improvementAreas")}</div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0", fontSize: "13px" }}>
                      <thead>
                        <tr>
                          <th style={{ ...tableHeaderStyle, borderTopLeftRadius: "10px" }}>{t("priority")}</th>
                          <th style={tableHeaderStyle}>{t("area")}</th>
                          <th style={tableHeaderStyle}>{t("issue")}</th>
                          <th style={tableHeaderStyle}>{t("impact")}</th>
                          <th style={{ ...tableHeaderStyle, borderTopRightRadius: "10px" }}>{t("recommendation")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {security_assessment.improvement_areas.map((area, i) => {
                          const isLast = i === security_assessment.improvement_areas.length - 1;
                          const priorityKey = area.priority?.toLowerCase();
                          const isKnownPriority = priorityKey && levelDisplayNames[priorityKey];
                          const priorityStyle = isKnownPriority ? getRiskLevelPriorityColors(priorityKey) : null;
                          const baseTd = { padding: "12px 16px", borderBottom: isLast ? "none" : "1px solid var(--card-border)", verticalAlign: "middle" };
                          return (
                            <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : "rgba(239, 68, 68, 0.03)" }}>
                              <td style={{ ...baseTd, ...(isLast ? { borderBottomLeftRadius: "10px" } : {}) }}>
                                <span style={{
                                  padding: "4px 10px",
                                  borderRadius: "999px",
                                  fontSize: "11px",
                                  fontWeight: 600,
                                  color: "white",
                                  background: priorityStyle ? priorityStyle.gradient : "#6b7280"
                                }}>
                                  {priorityStyle ? (levelDisplayNames[priorityKey] || area.priority) : t("unknown")}
                                </span>
                              </td>
                              <td style={{ ...baseTd, fontWeight: 600, color: "var(--card-text)" }}>{getAttackTypeLabel(area.area)}</td>
                              <td style={{ ...baseTd, color: "var(--card-text)", lineHeight: 1.5 }}>{area.issue || t("notAvailable")}</td>
                              <td style={{ ...baseTd, color: "var(--card-text)", lineHeight: 1.5 }}>{area.impact || t("notAvailable")}</td>
                              <td style={{ ...baseTd, color: "#3b82f6", lineHeight: 1.5, ...(isLast ? { borderBottomRightRadius: "10px" } : {}) }}>{area.recommendation || t("notAvailable")}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            {security_assessment.category_metrics && Object.keys(security_assessment.category_metrics).length > 0 && (
              <div>
                <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted-text)", marginBottom: "12px", fontWeight: 600 }}>{t("categoryMetrics")}</div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0", fontSize: "13px" }}>
                    <thead>
                      <tr>
                        <th style={{ ...tableHeaderStyle, borderTopLeftRadius: "10px" }}>{t("category")}</th>
                        <th style={tableHeaderStyle}>{t("tests")}</th>
                        <th style={tableHeaderStyle}>{t("failed")}</th>
                        <th style={tableHeaderStyle}>{t("passRate")}</th>
                        <th style={tableHeaderStyle}>{t("totalRequests")}</th>
                        <th style={tableHeaderStyle}>{t("throughput")}</th>
                        <th style={tableHeaderStyle}>{t("cost")}</th>
                        <th style={tableHeaderStyle}>{t("errorRate")}</th>
                        <th style={tableHeaderStyle}>{t("attackTypes")}</th>
                        <th style={{ ...tableHeaderStyle, borderTopRightRadius: "10px" }}>{t("riskLevels")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(security_assessment.category_metrics).map(([cat, m], i) => {
                        const isLast = i === Object.keys(security_assessment.category_metrics).length - 1;
                        const baseTd = { padding: "12px 16px", borderBottom: isLast ? "none" : "1px solid var(--card-border)", verticalAlign: "middle" };
                        return (
                          <tr key={cat} style={{ background: i % 2 === 0 ? "transparent" : "rgba(59, 130, 246, 0.03)" }}>
                            <td style={{ ...baseTd, fontWeight: 600, color: "var(--card-text)", ...(isLast ? { borderBottomLeftRadius: "10px" } : {}) }}>{cat.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</td>
                            <td style={baseTd}>{m.tests_count != null ? m.tests_count : t("notAvailable")}</td>
                            <td style={baseTd}>{m.failed_count != null ? m.failed_count : t("notAvailable")}</td>
                            <td style={baseTd}>{m.pass_rate != null ? `${Number(m.pass_rate).toFixed(1)}%` : t("notAvailable")}</td>
                            <td style={baseTd}>{m.total_requests != null ? m.total_requests : t("notAvailable")}</td>
                            <td style={baseTd}>{m.avg_throughput != null ? m.avg_throughput.toFixed(2) : t("notAvailable")}</td>
                            <td style={baseTd}>{m.total_cost != null ? `$${Number(m.total_cost).toFixed(3)}` : t("notAvailable")}</td>
                            <td style={baseTd}>{m.avg_error_rate != null ? (typeof m.avg_error_rate === "number" ? (m.avg_error_rate * 100).toFixed(1) + "%" : m.avg_error_rate) : t("notAvailable")}</td>
                            <td style={baseTd}>{m.attack_types?.map(getAttackTypeLabel).join(", ") || t("notAvailable")}</td>
                            <td style={{ ...baseTd, ...(isLast ? { borderBottomRightRadius: "10px" } : {}) }}>
                              {m.risk_levels?.length ? (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                                  {m.risk_levels.map((r) => {
                                    const rKey = r?.toLowerCase();
                                    const style = getRiskLevelPriorityColors(rKey);
                                    return (
                                      <span
                                        key={rKey}
                                        style={{
                                          padding: "4px 10px",
                                          borderRadius: "999px",
                                          fontSize: "11px",
                                          fontWeight: 600,
                                          color: "white",
                                          background: style.gradient
                                        }}
                                      >
                                        {levelDisplayNames[rKey] || r}
                                      </span>
                                    );
                                  })}
                                </div>
                              ) : t("notAvailable")}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {(security_assessment.metric_definitions || security_assessment.request_metrics) && (
              <div style={{
                display: "grid",
                gridTemplateColumns: security_assessment.metric_definitions && security_assessment.request_metrics ? "repeat(2, minmax(0, 1fr))" : "1fr",
                gap: "20px"
              }}>
                {security_assessment.metric_definitions && (
                  <div>
                    <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted-text)", marginBottom: "12px", fontWeight: 600 }}>{t("metricDefinitions")}</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                      {security_assessment.metric_definitions.request_level && (
                        <div style={{ padding: "14px 16px", background: "rgba(59, 130, 246, 0.06)", borderRadius: "10px", border: "1px solid rgba(59, 130, 246, 0.12)" }}>
                          <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#3b82f6", fontWeight: 600, marginBottom: "6px" }}>{t("requestLevel")}</div>
                          <div style={{ fontSize: "14px", color: "var(--card-text)", lineHeight: 1.55 }}>{security_assessment.metric_definitions.request_level}</div>
                        </div>
                      )}
                      {security_assessment.metric_definitions.scenario_level && (
                        <div style={{ padding: "14px 16px", background: "rgba(139, 92, 246, 0.06)", borderRadius: "10px", border: "1px solid rgba(139, 92, 246, 0.12)" }}>
                          <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#8b5cf6", fontWeight: 600, marginBottom: "6px" }}>{t("scenarioLevel")}</div>
                          <div style={{ fontSize: "14px", color: "var(--card-text)", lineHeight: 1.55 }}>{security_assessment.metric_definitions.scenario_level}</div>
                        </div>
                      )}
                      {security_assessment.metric_definitions.warning && (
                        <div style={{ padding: "14px 16px", background: "rgba(245, 158, 11, 0.08)", borderRadius: "10px", border: "1px solid rgba(245, 158, 11, 0.2)" }}>
                          <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#f59e0b", fontWeight: 600, marginBottom: "6px" }}>{t("warning")}</div>
                          <div style={{ fontSize: "14px", color: "var(--card-text)", lineHeight: 1.55 }}>{security_assessment.metric_definitions.warning}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {security_assessment.request_metrics && (
                  <div>
                    <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted-text)", marginBottom: "12px", fontWeight: 600 }}>{t("requestMetrics")}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px", marginBottom: "12px" }}>
                      <div style={{ background: "rgba(59, 130, 246, 0.08)", borderRadius: "12px", padding: "16px", textAlign: "center", border: "1px solid rgba(59, 130, 246, 0.15)" }}>
                        <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#3b82f6", fontWeight: 600 }}>{t("totalTested")}</div>
                        <div style={{ fontSize: "22px", fontWeight: 700, color: "var(--card-text)", marginTop: "6px" }}>{security_assessment.request_metrics.total_requests_tested ?? t("notAvailable")}</div>
                      </div>
                      <div style={{ background: "rgba(239, 68, 68, 0.08)", borderRadius: "12px", padding: "16px", textAlign: "center", border: "1px solid rgba(239, 68, 68, 0.15)" }}>
                        <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#ef4444", fontWeight: 600 }}>{t("failed")}</div>
                        <div style={{ fontSize: "22px", fontWeight: 700, color: "var(--card-text)", marginTop: "6px" }}>{security_assessment.request_metrics.failed_requests ?? t("notAvailable")}</div>
                      </div>
                      <div style={{ background: "rgba(34, 197, 94, 0.08)", borderRadius: "12px", padding: "16px", textAlign: "center", border: "1px solid rgba(34, 197, 94, 0.15)" }}>
                        <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#22c55e", fontWeight: 600 }}>{t("readiness")}</div>
                        <div style={{ fontSize: "22px", fontWeight: 700, color: "var(--card-text)", marginTop: "6px" }}>
                          {security_assessment.request_metrics.request_level_readiness != null
                            ? `${Number(security_assessment.request_metrics.request_level_readiness).toFixed(2)}%`
                            : t("notAvailable")}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                      {security_assessment.request_metrics.source_overall_risk_level && (() => {
                        const riskKey = security_assessment.request_metrics.source_overall_risk_level.toLowerCase();
                        const riskStyle = getRiskLevelPriorityColors(riskKey);
                        return (
                          <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px", background: "rgba(59, 130, 246, 0.06)", borderRadius: "10px", border: "1px solid rgba(59, 130, 246, 0.12)" }}>
                            <span style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted-text)", fontWeight: 600 }}>{t("sourceOverallRiskLevel")}</span>
                            <span style={{ padding: "4px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: 600, color: "white", background: riskStyle.gradient }}>
                              {levelDisplayNames[riskKey] || security_assessment.request_metrics.source_overall_risk_level}
                            </span>
                          </div>
                        );
                      })()}
                      {security_assessment.request_metrics.source_risk_warning && (
                        <div style={{ padding: "14px 16px", background: "rgba(245, 158, 11, 0.08)", borderRadius: "10px", border: "1px solid rgba(245, 158, 11, 0.2)" }}>
                          <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#f59e0b", fontWeight: 600, marginBottom: "6px" }}>{t("sourceRiskWarning")}</div>
                          <div style={{ fontSize: "14px", color: "var(--card-text)", lineHeight: 1.55 }}>{security_assessment.request_metrics.source_risk_warning}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </SectionCardAccordion>
      )}

      {/* Section 3: Recommendations */}
      {recommendations && (
        <SectionCardAccordion title={t("recommendations")} icon={<i className="fas fa-lightbulb"></i>}>
          <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            {recommendations.critical_immediate?.length > 0 && (
              <div style={{ background: "rgba(239, 68, 68, 0.06)", borderRadius: "12px", padding: "16px", border: "1px solid rgba(239, 68, 68, 0.15)" }}>
                <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#ef4444", marginBottom: "12px", fontWeight: 700 }}>{t("criticalImmediate")}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  {recommendations.critical_immediate.map((item, i) => (
                    <div key={i} style={{ border: "1px solid var(--card-border)", borderRadius: "14px", overflow: "hidden", background: "var(--card-bg)" }}>
                      <div style={{ padding: "16px 20px", background: "linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(245, 158, 11, 0.1))", borderBottom: "1px solid var(--card-border)", display: "flex", alignItems: "flex-start", gap: "12px" }}>
                        <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: "rgba(239, 68, 68, 0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <i className="fas fa-exclamation-triangle" style={{ fontSize: "14px", color: "#ef4444" }}></i>
                        </div>
                        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "6px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                            <span style={{ fontSize: "16px", fontWeight: 600, color: "var(--card-text)" }}>{getAttackTypeLabel(item.attack_type)}</span>
                            {item.risk_level && (
                              <span style={{
                                padding: "4px 10px",
                                borderRadius: "999px",
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "white",
                                background: getRiskLevelPriorityColors(item.risk_level?.toLowerCase()).gradient
                              }}>
                                {levelDisplayNames[item.risk_level?.toLowerCase()] || item.risk_level} {t("risk")}
                              </span>
                            )}
                          </div>
                          {item.recommendation && <div style={{ fontSize: "14px", color: "var(--card-text)", lineHeight: 1.5 }}>{item.recommendation}</div>}
                          {item.details && <div style={{ fontSize: "11px", color: "#9ca3af", lineHeight: 1.4 }}>{item.details}</div>}
                        </div>
                      </div>
                      <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "16px" }}>
                        {(item.error_rate != null || item.failed_count != null || item.total_requests != null) && (
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
                            {item.error_rate != null && (
                              <div style={{ background: "rgba(239, 68, 68, 0.08)", borderRadius: "8px", padding: "12px", textAlign: "center", border: "1px solid rgba(239, 68, 68, 0.15)" }}>
                                <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#ef4444", fontWeight: 600 }}>{t("errorRate")}</div>
                                <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--card-text)", marginTop: "4px" }}>{(item.error_rate * 100).toFixed(1)}%</div>
                              </div>
                            )}
                            {item.failed_count != null && (
                              <div style={{ background: "rgba(245, 158, 11, 0.08)", borderRadius: "8px", padding: "12px", textAlign: "center", border: "1px solid rgba(245, 158, 11, 0.15)" }}>
                                <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#f59e0b", fontWeight: 600 }}>{t("failed")}</div>
                                <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--card-text)", marginTop: "4px" }}>{item.failed_count}</div>
                              </div>
                            )}
                            {item.total_requests != null && (
                              <div style={{ background: "rgba(59, 130, 246, 0.08)", borderRadius: "8px", padding: "12px", textAlign: "center", border: "1px solid rgba(59, 130, 246, 0.15)" }}>
                                <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#3b82f6", fontWeight: 600 }}>{t("totalRequests")}</div>
                                <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--card-text)", marginTop: "4px" }}>{item.total_requests}</div>
                              </div>
                            )}
                          </div>
                        )}
                        <div style={{ background: "rgba(59, 130, 246, 0.04)", borderRadius: "12px", padding: "16px", border: "1px solid rgba(59, 130, 246, 0.12)" }}>
                          <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#3b82f6", marginBottom: "10px", fontWeight: 600 }}>{t("implementation")}</div>
                          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                            {item.implementation?.map((step, j) => (
                              <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "14px", color: "var(--card-text)" }}>
                                <i className="fas fa-circle" style={{ fontSize: "6px", color: "#3b82f6", marginTop: "7px", flexShrink: 0 }}></i>
                                {step}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(recommendations.medium_priority?.length > 0 || recommendations.low_priority?.length > 0) && (
              <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                {recommendations.medium_priority?.length > 0 && (
                  <div style={{ background: "rgba(245, 158, 11, 0.06)", borderRadius: "12px", padding: "16px", border: "1px solid rgba(245, 158, 11, 0.15)" }}>
                    <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#f59e0b", marginBottom: "12px", fontWeight: 600 }}>{t("mediumPriority")}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: "12px" }}>
                      {recommendations.medium_priority.map((item, i) => (
                        <div
                          key={i}
                          style={{
                            borderRadius: "12px",
                            overflow: "hidden",
                            background: "var(--card-bg)",
                            border: "1px solid rgba(245, 158, 11, 0.35)",
                            boxShadow: "0 4px 14px rgba(0,0,0,0.08)"
                          }}
                        >
                          {(item.attack_type || item.category) && (
                            <div style={{ padding: "10px 14px", background: "rgba(245, 158, 11, 0.12)", borderBottom: "1px solid rgba(245, 158, 11, 0.2)", fontSize: "14px", fontWeight: 600, color: "var(--card-text)" }}>
                              {getAttackTypeLabel(item.attack_type || item.category)}
                            </div>
                          )}
                          <div style={{ padding: "14px", display: "flex", flexDirection: "column", gap: "10px", fontSize: "13px", color: "var(--card-text)", lineHeight: 1.5 }}>
                            {item.issue && (
                              <div>
                                <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", marginBottom: "2px", fontWeight: 600 }}>{t("issue")}</div>
                                <div>{item.issue}</div>
                              </div>
                            )}
                            {item.impact && (
                              <div>
                                <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", marginBottom: "2px", fontWeight: 600 }}>{t("impact")}</div>
                                <div>{item.impact}</div>
                              </div>
                            )}
                            {item.estimated_savings && (
                              <div>
                                <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", marginBottom: "2px", fontWeight: 600 }}>{t("estimatedSavings")}</div>
                                <div style={{ color: "#15803d", fontWeight: 600 }}>{item.estimated_savings}</div>
                              </div>
                            )}
                            {(item.recommendation || item.title) && (
                              <div>
                                <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", marginBottom: "2px", fontWeight: 600 }}>{t("recommendation")}</div>
                                <div>{item.recommendation || item.title}</div>
                              </div>
                            )}
                            {item.recommendations?.length > 0 && (
                              <div>
                                <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", marginBottom: "6px", fontWeight: 600 }}>{t("actions")}</div>
                                <ul style={{ margin: 0, paddingLeft: "18px", display: "flex", flexDirection: "column", gap: "4px" }}>
                                  {item.recommendations.map((r, j) => (
                                    <li key={j}>{r}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {recommendations.low_priority?.length > 0 && (
                  <div style={{ background: "rgba(34, 197, 94, 0.06)", borderRadius: "12px", padding: "16px", border: "1px solid rgba(34, 197, 94, 0.15)" }}>
                    <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#22c55e", marginBottom: "12px", fontWeight: 600 }}>{t("lowPriority")}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: "12px" }}>
                      {recommendations.low_priority.map((item, i) => (
                        <div
                          key={i}
                          style={{
                            borderRadius: "12px",
                            overflow: "hidden",
                            background: "var(--card-bg)",
                            border: "1px solid rgba(34, 197, 94, 0.35)",
                            boxShadow: "0 4px 14px rgba(0,0,0,0.08)"
                          }}
                        >
                          {(item.attack_type || item.category) && (
                            <div style={{ padding: "10px 14px", background: "rgba(34, 197, 94, 0.12)", borderBottom: "1px solid rgba(34, 197, 94, 0.2)", fontSize: "14px", fontWeight: 600, color: "var(--card-text)" }}>
                              {getAttackTypeLabel(item.attack_type || item.category)}
                            </div>
                          )}
                          <div style={{ padding: "14px", display: "flex", flexDirection: "column", gap: "10px", fontSize: "13px", color: "var(--card-text)", lineHeight: 1.5 }}>
                            {item.issue && (
                              <div>
                                <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", marginBottom: "2px", fontWeight: 600 }}>{t("issue")}</div>
                                <div>{item.issue}</div>
                              </div>
                            )}
                            {item.impact && (
                              <div>
                                <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", marginBottom: "2px", fontWeight: 600 }}>{t("impact")}</div>
                                <div>{item.impact}</div>
                              </div>
                            )}
                            {item.estimated_savings && (
                              <div>
                                <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", marginBottom: "2px", fontWeight: 600 }}>{t("estimatedSavings")}</div>
                                <div style={{ color: "#15803d", fontWeight: 600 }}>{item.estimated_savings}</div>
                              </div>
                            )}
                            {(item.recommendation || item.title) && (
                              <div>
                                <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", marginBottom: "2px", fontWeight: 600 }}>{t("recommendation")}</div>
                                <div>{item.recommendation || item.title}</div>
                              </div>
                            )}
                            {item.recommendations?.length > 0 && (
                              <div>
                                <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", marginBottom: "6px", fontWeight: 600 }}>{t("actions")}</div>
                                <ul style={{ margin: 0, paddingLeft: "18px", display: "flex", flexDirection: "column", gap: "4px" }}>
                                  {item.recommendations.map((r, j) => (
                                    <li key={j}>{r}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {recommendations.monitoring?.length > 0 && (
              <div style={{ border: "1px solid var(--card-border)", borderRadius: "14px", overflow: "hidden" }}>
                <div style={{ padding: "16px 20px", background: "linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1))", borderBottom: "1px solid var(--card-border)" }}>
                  <div style={{ fontSize: "16px", fontWeight: 600, color: "var(--card-text)" }}>{t("monitoring")}</div>
                </div>
                <div
                  style={{
                    padding: "20px",
                    display: "grid",
                    gridTemplateColumns: monitoringCount === 1 ? "1fr 1fr" : "repeat(auto-fit, minmax(420px, 1fr))",
                    gap: "20px"
                  }}
                >
                  {recommendations.monitoring.map((mon, idx) => (
                    <div key={idx} style={{ border: "1px solid var(--card-border)", borderRadius: "12px", overflow: "hidden", background: "var(--card-bg)" }}>
                      {(mon.title || mon.priority) && (
                        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--card-border)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
                          {mon.title && <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--card-text)" }}>{mon.title}</div>}
                          {mon.priority && (
                            <span style={{ padding: "4px 10px", borderRadius: "999px", fontSize: "11px", fontWeight: 600, color: "white", background: getRiskLevelPriorityColors(mon.priority?.toLowerCase()).gradient }}>
                              {levelDisplayNames[mon.priority?.toLowerCase()] || mon.priority} {t("priority")}
                            </span>
                          )}
                        </div>
                      )}
                      <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: "16px" }}>
                        {mon.metrics?.length > 0 && (
                          <div>
                            <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#3b82f6", marginBottom: "8px", fontWeight: 600 }}>{t("metrics")}</div>
                            <div style={{ overflowX: "auto" }}>
                              <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0", fontSize: "13px" }}>
                                <thead>
                                  <tr>
                                    <th style={tableHeaderStyle}>{t("metric")}</th>
                                    <th style={tableHeaderStyle}>{t("threshold")}</th>
                                    <th style={tableHeaderStyle}>{t("action")}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {mon.metrics.map((row, i) => (
                                    <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : "rgba(59, 130, 246, 0.03)" }}>
                                      <td style={{ padding: "10px 12px", borderBottom: "1px solid var(--card-border)", color: "var(--card-text)" }}>{row.metric}</td>
                                      <td style={{ padding: "10px 12px", borderBottom: "1px solid var(--card-border)", color: "var(--card-text)" }}>{row.threshold}</td>
                                      <td style={{ padding: "10px 12px", borderBottom: "1px solid var(--card-border)", color: "var(--card-text)" }}>{row.action}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                        {(mon.tools?.length > 0 || mon.dashboards?.length > 0) && (
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px" }}>
                            {mon.tools?.length > 0 && (
                              <div>
                                <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#8b5cf6", marginBottom: "6px", fontWeight: 600 }}>{t("tools")}</div>
                                <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "14px", color: "var(--card-text)", lineHeight: 1.6 }}>
                                  {mon.tools.map((tool, i) => (
                                    <li key={i}>{tool}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {mon.dashboards?.length > 0 && (
                              <div>
                                <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#8b5cf6", marginBottom: "6px", fontWeight: 600 }}>{t("dashboards")}</div>
                                <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "14px", color: "var(--card-text)", lineHeight: 1.6 }}>
                                  {mon.dashboards.map((d, i) => (
                                    <li key={i}>{d}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {recommendations.maintenance?.length > 0 && (
              <div style={{ border: "1px solid var(--card-border)", borderRadius: "14px", overflow: "hidden" }}>
                <div style={{ padding: "16px 20px", background: "linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.1))", borderBottom: "1px solid var(--card-border)" }}>
                  <div style={{ fontSize: "16px", fontWeight: 600, color: "var(--card-text)" }}>{t("maintenance")}</div>
                </div>
                <div
                  style={{
                    padding: "20px",
                    display: "grid",
                    gridTemplateColumns: maintenanceCount === 1 ? "1fr 1fr" : "repeat(auto-fit, minmax(420px, 1fr))",
                    gap: "20px"
                  }}
                >
                  {recommendations.maintenance.map((maint, idx) => (
                    <div key={idx} style={{ border: "1px solid var(--card-border)", borderRadius: "12px", overflow: "hidden", background: "var(--card-bg)" }}>
                      {(maint.title || maint.priority) && (
                        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--card-border)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
                          {maint.title && <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--card-text)" }}>{maint.title}</div>}
                          {maint.priority && (
                            <span style={{ padding: "4px 10px", borderRadius: "999px", fontSize: "11px", fontWeight: 600, color: "white", background: getRiskLevelPriorityColors(maint.priority?.toLowerCase()).gradient }}>
                              {levelDisplayNames[maint.priority?.toLowerCase()] || maint.priority} {t("priority")}
                            </span>
                          )}
                        </div>
                      )}
                      <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: "16px" }}>
                        {maint.frequency && Object.keys(maint.frequency).length > 0 && (
                          <div>
                            <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#8b5cf6", marginBottom: "8px", fontWeight: 600 }}>{t("frequency")}</div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                              {Object.entries(maint.frequency).map(([k, v]) => (
                                <span key={k} style={{ padding: "6px 12px", background: "rgba(139, 92, 246, 0.12)", borderRadius: "8px", fontSize: "13px", color: "var(--card-text)", border: "1px solid rgba(139, 92, 246, 0.2)" }}>{k.replace(/_/g, " ")}: {v}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {maint.test_plan?.length > 0 && (
                          <div>
                            <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#8b5cf6", marginBottom: "8px", fontWeight: 600 }}>{t("testPlan")}</div>
                            <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "14px", color: "var(--card-text)", lineHeight: 1.6 }}>
                              {maint.test_plan.map((step, i) => <li key={i}>{step}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </SectionCardAccordion>
      )}

      {external_ddos_solutions && (
        <SectionCardAccordion
          title={t("externalDdosSolutions")}
          icon={<i className="fas fa-cloud"></i>}
        >
          <div style={{
            padding: "20px",
            borderRadius: "12px",
            border: "1px solid rgba(59, 130, 246, 0.18)",
            background: "rgba(59, 130, 246, 0.06)",
            display: "flex",
            flexDirection: "column",
            gap: "10px"
          }}>
            {external_ddos_solutions.title && (
              <div style={{ fontSize: "16px", fontWeight: 600, color: "var(--card-text)" }}>
                {external_ddos_solutions.title}
              </div>
            )}
            {external_ddos_solutions.architecture_principle && (
              <div style={{ fontSize: "14px", color: "var(--card-text)", lineHeight: 1.6 }}>
                {external_ddos_solutions.architecture_principle}
              </div>
            )}
          </div>

          {(external_ddos_solutions.important_limitations?.length > 0 ||
            external_ddos_solutions.rollout_plan?.length > 0 ||
            external_ddos_solutions.success_metrics?.length > 0) && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "16px" }}>
              {external_ddos_solutions.important_limitations?.length > 0 && (
                <div style={{ border: "1px solid var(--card-border)", borderRadius: "12px", overflow: "hidden", background: "var(--card-bg)" }}>
                  <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--card-border)", background: "rgba(245, 158, 11, 0.08)" }}>
                    <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#f59e0b", fontWeight: 600 }}>{t("importantLimitations")}</div>
                  </div>
                  <ul style={{ margin: 0, padding: "16px 16px 16px 32px", fontSize: "14px", color: "var(--card-text)", lineHeight: 1.6, display: "flex", flexDirection: "column", gap: "8px" }}>
                    {external_ddos_solutions.important_limitations.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
              {external_ddos_solutions.rollout_plan?.length > 0 && (
                <div style={{ border: "1px solid var(--card-border)", borderRadius: "12px", overflow: "hidden", background: "var(--card-bg)" }}>
                  <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--card-border)", background: "rgba(59, 130, 246, 0.08)" }}>
                    <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#3b82f6", fontWeight: 600 }}>{t("rolloutPlan")}</div>
                  </div>
                  <ul style={{ margin: 0, padding: "16px 16px 16px 32px", fontSize: "14px", color: "var(--card-text)", lineHeight: 1.6, display: "flex", flexDirection: "column", gap: "8px" }}>
                    {external_ddos_solutions.rollout_plan.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
              {external_ddos_solutions.success_metrics?.length > 0 && (
                <div style={{ border: "1px solid var(--card-border)", borderRadius: "12px", overflow: "hidden", background: "var(--card-bg)" }}>
                  <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--card-border)", background: "rgba(34, 197, 94, 0.08)" }}>
                    <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#22c55e", fontWeight: 600 }}>{t("successMetrics")}</div>
                  </div>
                  <ul style={{ margin: 0, padding: "16px 16px 16px 32px", fontSize: "14px", color: "var(--card-text)", lineHeight: 1.6, display: "flex", flexDirection: "column", gap: "8px" }}>
                    {external_ddos_solutions.success_metrics.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {external_ddos_solutions.vendor_solutions?.length > 0 && (
            <div style={{ border: "1px solid var(--card-border)", borderRadius: "14px", overflow: "hidden" }}>
              <div style={{ padding: "16px 20px", background: "linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1))", borderBottom: "1px solid var(--card-border)" }}>
                <div style={{ fontSize: "16px", fontWeight: 600, color: "var(--card-text)" }}>{t("vendorSolutions")}</div>
              </div>
              <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "16px" }}>
                {external_ddos_solutions.vendor_solutions.map((vendorSol, i) => (
                  <div
                    key={i}
                    style={{
                      border: "1px solid var(--card-border)",
                      borderRadius: "14px",
                      overflow: "hidden",
                      background: "var(--card-bg)",
                      display: "flex",
                      flexDirection: "column"
                    }}
                  >
                    <div style={{
                      padding: "14px 18px",
                      background: "linear-gradient(135deg, rgba(59, 130, 246, 0.12), rgba(139, 92, 246, 0.1))",
                      borderBottom: "1px solid var(--card-border)",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px"
                    }}>
                      <div style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "8px",
                        background: "rgba(59, 130, 246, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0
                      }}>
                        <i className="fas fa-building" style={{ fontSize: "14px", color: "#3b82f6" }}></i>
                      </div>
                      <div style={{ fontSize: "16px", fontWeight: 600, color: "var(--card-text)" }}>
                        {vendorSol.vendor || t("vendor")}
                      </div>
                    </div>

                    <div style={{ padding: "18px", display: "flex", flexDirection: "column", gap: "16px", flex: 1 }}>
                      {vendorSol.recommended_services?.length > 0 && (
                        <div>
                          <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#8b5cf6", marginBottom: "8px", fontWeight: 600 }}>
                            {t("recommendedServices")}
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                            {vendorSol.recommended_services.map((service, j) => (
                              <span
                                key={j}
                                style={{
                                  padding: "6px 12px",
                                  borderRadius: "8px",
                                  fontSize: "13px",
                                  fontWeight: 500,
                                  color: "var(--card-text)",
                                  background: "rgba(139, 92, 246, 0.1)",
                                  border: "1px solid rgba(139, 92, 246, 0.2)"
                                }}
                              >
                                {service}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {vendorSol.configuration_steps?.length > 0 && (
                        <div>
                          <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#3b82f6", marginBottom: "8px", fontWeight: 600 }}>
                            {t("configurationSteps")}
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                            {vendorSol.configuration_steps.map((step, j) => (
                              <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "14px", color: "var(--card-text)", lineHeight: 1.5 }}>
                                <span style={{
                                  minWidth: "20px",
                                  height: "20px",
                                  borderRadius: "6px",
                                  background: "rgba(59, 130, 246, 0.15)",
                                  color: "#3b82f6",
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  marginTop: "1px",
                                  flexShrink: 0
                                }}>
                                  {j + 1}
                                </span>
                                {step}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {vendorSol.ai_specific_notes?.length > 0 && (
                        <div style={{
                          background: "rgba(245, 158, 11, 0.06)",
                          borderRadius: "10px",
                          padding: "12px 14px",
                          border: "1px solid rgba(245, 158, 11, 0.18)"
                        }}>
                          <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#f59e0b", marginBottom: "8px", fontWeight: 600 }}>
                            {t("aiSpecificNotes")}
                          </div>
                          <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "13px", color: "var(--card-text)", lineHeight: 1.55, display: "flex", flexDirection: "column", gap: "6px" }}>
                            {vendorSol.ai_specific_notes.map((note, j) => (
                              <li key={j}>{note}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {vendorSol.official_references?.length > 0 && (
                        <div>
                          <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#22c55e", marginBottom: "8px", fontWeight: 600 }}>
                            {t("officialReferences")}
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                            {vendorSol.official_references.map((url, j) => (
                              <a
                                key={j}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  fontSize: "13px",
                                  color: "#3b82f6",
                                  textDecoration: "none",
                                  wordBreak: "break-all",
                                  lineHeight: 1.4
                                }}
                              >
                                <i className="fas fa-external-link-alt" style={{ fontSize: "10px", marginRight: "6px", opacity: 0.8 }}></i>
                                {url}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {external_ddos_solutions.common_ai_ddos_controls?.length > 0 && (
            <div style={{ border: "1px solid var(--card-border)", borderRadius: "14px", overflow: "hidden" }}>
              <div style={{ padding: "16px 20px", background: "linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.1))", borderBottom: "1px solid var(--card-border)" }}>
                <div style={{ fontSize: "16px", fontWeight: 600, color: "var(--card-text)" }}>{t("commonAiDdosControls")}</div>
              </div>
              <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "16px" }}>
                {external_ddos_solutions.common_ai_ddos_controls.map((ctrl, i) => (
                  <div
                    key={i}
                    style={{
                      border: "1px solid var(--card-border)",
                      borderRadius: "14px",
                      overflow: "hidden",
                      background: "var(--card-bg)",
                      display: "flex",
                      flexDirection: "column"
                    }}
                  >
                    <div style={{
                      padding: "14px 18px",
                      background: "linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.08))",
                      borderBottom: "1px solid var(--card-border)",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px"
                    }}>
                      <div style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "8px",
                        background: "rgba(139, 92, 246, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0
                      }}>
                        <i className="fas fa-shield-alt" style={{ fontSize: "14px", color: "#8b5cf6" }}></i>
                      </div>
                      <div style={{ fontSize: "16px", fontWeight: 600, color: "var(--card-text)" }}>
                        {ctrl.control || t("control")}
                      </div>
                    </div>

                    <div style={{ padding: "18px", display: "flex", flexDirection: "column", gap: "16px" }}>
                      {ctrl.why_for_ai && (
                        <div style={{
                          background: "rgba(59, 130, 246, 0.06)",
                          borderRadius: "10px",
                          padding: "12px 14px",
                          border: "1px solid rgba(59, 130, 246, 0.15)"
                        }}>
                          <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#3b82f6", marginBottom: "6px", fontWeight: 600 }}>
                            {t("whyForAi")}
                          </div>
                          <div style={{ fontSize: "14px", color: "var(--card-text)", lineHeight: 1.55 }}>
                            {ctrl.why_for_ai}
                          </div>
                        </div>
                      )}

                      {ctrl.configuration?.length > 0 && (
                        <div>
                          <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#3b82f6", marginBottom: "8px", fontWeight: 600 }}>
                            {t("configuration")}
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                            {ctrl.configuration.map((step, j) => (
                              <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "14px", color: "var(--card-text)", lineHeight: 1.5 }}>
                                <span style={{
                                  minWidth: "20px",
                                  height: "20px",
                                  borderRadius: "6px",
                                  background: "rgba(59, 130, 246, 0.15)",
                                  color: "#3b82f6",
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  marginTop: "1px",
                                  flexShrink: 0
                                }}>
                                  {j + 1}
                                </span>
                                {step}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {external_ddos_solutions.assessment_basis && (
            <div style={{ border: "1px solid var(--card-border)", borderRadius: "14px", overflow: "hidden" }}>
              <div style={{ padding: "16px 20px", background: "linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(34, 197, 94, 0.1))", borderBottom: "1px solid var(--card-border)" }}>
                <div style={{ fontSize: "16px", fontWeight: 600, color: "var(--card-text)" }}>{t("assessmentBasis")}</div>
              </div>
              <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "20px" }}>
                {(external_ddos_solutions.assessment_basis.observed_failure_count != null ||
                  external_ddos_solutions.assessment_basis.profile?.readiness_score != null ||
                  external_ddos_solutions.assessment_basis.profile?.observed_failure_rate_percent != null ||
                  external_ddos_solutions.assessment_basis.profile?.recommended_protection_tier) && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "12px" }}>
                    {external_ddos_solutions.assessment_basis.observed_failure_count != null && (
                      <div style={{ background: "rgba(239, 68, 68, 0.08)", borderRadius: "12px", padding: "16px", textAlign: "center", border: "1px solid rgba(239, 68, 68, 0.15)" }}>
                        <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#ef4444", fontWeight: 600 }}>{t("observedFailures")}</div>
                        <div style={{ fontSize: "24px", fontWeight: 700, color: "var(--card-text)", marginTop: "6px" }}>
                          {external_ddos_solutions.assessment_basis.observed_failure_count}
                        </div>
                      </div>
                    )}
                    {external_ddos_solutions.assessment_basis.profile?.observed_failure_rate_percent != null && (
                      <div style={{ background: "rgba(245, 158, 11, 0.08)", borderRadius: "12px", padding: "16px", textAlign: "center", border: "1px solid rgba(245, 158, 11, 0.15)" }}>
                        <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#f59e0b", fontWeight: 600 }}>{t("failureRate")}</div>
                        <div style={{ fontSize: "24px", fontWeight: 700, color: "var(--card-text)", marginTop: "6px" }}>
                          {Number(external_ddos_solutions.assessment_basis.profile.observed_failure_rate_percent).toFixed(1)}%
                        </div>
                      </div>
                    )}
                    {external_ddos_solutions.assessment_basis.profile?.readiness_score != null && (
                      <div style={{ background: "rgba(59, 130, 246, 0.08)", borderRadius: "12px", padding: "16px", textAlign: "center", border: "1px solid rgba(59, 130, 246, 0.15)" }}>
                        <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#3b82f6", fontWeight: 600 }}>{t("readinessScore")}</div>
                        <div style={{ fontSize: "24px", fontWeight: 700, color: "var(--card-text)", marginTop: "6px" }}>
                          {Number(external_ddos_solutions.assessment_basis.profile.readiness_score).toFixed(1)}%
                        </div>
                      </div>
                    )}
                    {external_ddos_solutions.assessment_basis.profile?.recommended_protection_tier && (
                      <div style={{ background: "rgba(34, 197, 94, 0.08)", borderRadius: "12px", padding: "16px", textAlign: "center", border: "1px solid rgba(34, 197, 94, 0.15)" }}>
                        <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#22c55e", fontWeight: 600 }}>{t("protectionTier")}</div>
                        <div style={{ fontSize: "20px", fontWeight: 700, color: "var(--card-text)", marginTop: "6px", textTransform: "capitalize" }}>
                          {external_ddos_solutions.assessment_basis.profile.recommended_protection_tier}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {external_ddos_solutions.assessment_basis.failed_subcategories &&
                  Object.keys(external_ddos_solutions.assessment_basis.failed_subcategories).length > 0 && (
                  <div>
                    <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#ef4444", marginBottom: "10px", fontWeight: 600 }}>
                      {t("failedSubcategories")}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                      {Object.entries(external_ddos_solutions.assessment_basis.failed_subcategories).map(([key, count]) => (
                        <div
                          key={key}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            padding: "8px 12px",
                            borderRadius: "999px",
                            background: "rgba(239, 68, 68, 0.08)",
                            border: "1px solid rgba(239, 68, 68, 0.18)",
                            color: "var(--card-text)",
                            fontSize: "13px",
                            lineHeight: 1
                          }}
                        >
                          <span style={{ fontWeight: 600 }}>{getAttackTypeLabel(key)}</span>
                          <span style={{ width: "1px", height: "14px", background: "rgba(239, 68, 68, 0.25)" }} />
                          <span style={{ fontWeight: 700, color: "#ef4444" }}>{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {external_ddos_solutions.assessment_basis.profile?.rollout_urgency && (
                  <div style={{
                    background: "rgba(59, 130, 246, 0.06)",
                    borderRadius: "10px",
                    padding: "14px 16px",
                    border: "1px solid rgba(59, 130, 246, 0.15)"
                  }}>
                    <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#3b82f6", marginBottom: "6px", fontWeight: 600 }}>
                      {t("rolloutUrgency")}
                    </div>
                    <div style={{ fontSize: "14px", color: "var(--card-text)", lineHeight: 1.55 }}>
                      {external_ddos_solutions.assessment_basis.profile.rollout_urgency}
                    </div>
                  </div>
                )}

                {external_ddos_solutions.assessment_basis.profile?.starting_thresholds_not_production_defaults && (() => {
                  const thresholds = external_ddos_solutions.assessment_basis.profile.starting_thresholds_not_production_defaults;
                  const thresholdCard = (label, val) => (
                    <div
                      style={{
                        padding: "12px 14px",
                        background: "rgba(59, 130, 246, 0.06)",
                        borderRadius: "10px",
                        border: "1px solid rgba(59, 130, 246, 0.12)"
                      }}
                    >
                      <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--muted-text)", fontWeight: 600, marginBottom: "4px" }}>
                        {label}
                      </div>
                      <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--card-text)" }}>
                        {typeof val === "number" ? val.toLocaleString() : val}
                      </div>
                    </div>
                  );
                  return (
                    <div>
                      <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#3b82f6", marginBottom: "10px", fontWeight: 600 }}>
                        {t("startingThresholds")}
                        <span style={{ marginLeft: "8px", fontSize: "11px", fontWeight: 500, color: "var(--muted-text)", textTransform: "none", letterSpacing: 0 }}>
                          {t("notProductionDefaults")}
                        </span>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "10px" }}>
                        {thresholds.authenticated_requests_per_minute_per_principal != null &&
                          thresholdCard(t("authenticatedRequestsPerMinute"), thresholds.authenticated_requests_per_minute_per_principal)}
                        {thresholds.concurrent_inferences_per_principal != null &&
                          thresholdCard(t("concurrentInferences"), thresholds.concurrent_inferences_per_principal)}
                        {thresholds.maximum_prompt_tokens != null &&
                          thresholdCard(t("maximumPromptTokens"), thresholds.maximum_prompt_tokens)}
                        {thresholds.maximum_request_body_bytes != null &&
                          thresholdCard(t("maximumRequestBodyBytes"), thresholds.maximum_request_body_bytes)}
                        {thresholds.maximum_request_runtime_seconds != null &&
                          thresholdCard(t("maximumRequestRuntimeSeconds"), thresholds.maximum_request_runtime_seconds)}
                        {thresholds.short_burst_requests_per_10_seconds_per_principal != null &&
                          thresholdCard(t("shortBurstRequests"), thresholds.short_burst_requests_per_10_seconds_per_principal)}
                      </div>
                    </div>
                  );
                })()}

                {external_ddos_solutions.assessment_basis.profile?.threshold_warning && (
                  <div style={{
                    background: "rgba(245, 158, 11, 0.08)",
                    borderRadius: "10px",
                    padding: "14px 16px",
                    border: "1px solid rgba(245, 158, 11, 0.2)"
                  }}>
                    <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#f59e0b", marginBottom: "6px", fontWeight: 600 }}>
                      {t("thresholdWarning")}
                    </div>
                    <div style={{ fontSize: "14px", color: "var(--card-text)", lineHeight: 1.55 }}>
                      {external_ddos_solutions.assessment_basis.profile.threshold_warning}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </SectionCardAccordion>
      )}

      {(test_metrics || metadata) && (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {/* Test Metrics */}
          {test_metrics && (
            <SectionCardAccordion title={t("testMetrics")} icon={<i className="fas fa-tachometer-alt"></i>}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px", marginBottom: "20px" }}>
                <div style={{ background: "rgba(59, 130, 246, 0.08)", borderRadius: "12px", padding: "20px", textAlign: "center", border: "1px solid rgba(59, 130, 246, 0.15)" }}>
                  <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#3b82f6", fontWeight: 600 }}>{t("totalCost")}</div>
                  <div style={{ fontSize: "24px", fontWeight: 700, color: "var(--card-text)", marginTop: "8px" }}>${test_metrics.total_cost != null ? test_metrics.total_cost.toFixed(2) : t("notAvailable")}</div>
                </div>
                <div style={{ background: "rgba(139, 92, 246, 0.08)", borderRadius: "12px", padding: "20px", textAlign: "center", border: "1px solid rgba(139, 92, 246, 0.15)" }}>
                  <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#8b5cf6", fontWeight: 600 }}>{t("totalDuration")}</div>
                  <div style={{ fontSize: "24px", fontWeight: 700, color: "var(--card-text)", marginTop: "8px" }}>{test_metrics.total_duration != null ? `${test_metrics.total_duration.toFixed(1)}s` : t("notAvailable")}</div>
                </div>
                <div style={{ background: "rgba(34, 197, 94, 0.08)", borderRadius: "12px", padding: "20px", textAlign: "center", border: "1px solid rgba(34, 197, 94, 0.15)" }}>
                  <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#22c55e", fontWeight: 600 }}>{t("avgResponseTime")}</div>
                  <div style={{ fontSize: "24px", fontWeight: 700, color: "var(--card-text)", marginTop: "8px" }}>{test_metrics.avg_response_time != null ? `${test_metrics.avg_response_time.toFixed(0)}ms` : t("notAvailable")}</div>
                </div>
              </div>
              {test_metrics.throughput_by_attack && Object.keys(test_metrics.throughput_by_attack).length > 0 && (
                <div>
                  <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted-text)", marginBottom: "12px", fontWeight: 600 }}>{t("throughputByAttack")}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                    {Object.entries(test_metrics.throughput_by_attack).map(([attack, val]) => (
                      <div
                        key={attack}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          padding: "8px 12px",
                          borderRadius: "999px",
                          background: "rgba(59, 130, 246, 0.08)",
                          border: "1px solid rgba(59, 130, 246, 0.18)",
                          color: "var(--card-text)",
                          fontSize: "13px",
                          lineHeight: 1
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>{getAttackTypeLabel(attack)}</span>
                        <span style={{ width: "1px", height: "14px", background: "rgba(59, 130, 246, 0.25)" }} />
                        <span style={{ fontWeight: 700, color: "#3b82f6" }}>{typeof val === "number" ? val.toFixed(3) : val}</span>
                        <span style={{ color: "var(--muted-text)", fontSize: "12px" }}>{t("reqPerSec")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </SectionCardAccordion>
          )}

          {/* Metadata */}
          {metadata && (
            <SectionCardAccordion title={t("metadata")} icon={<i className="fas fa-info-circle"></i>}>
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
                  <div style={{ padding: "14px 16px", background: "rgba(59, 130, 246, 0.06)", borderRadius: "10px", border: "1px solid rgba(59, 130, 246, 0.12)" }}>
                    <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#3b82f6", fontWeight: 600 }}>{t("reportGenerated")}</div>
                    <div style={{ fontSize: "14px", color: "var(--card-text)", marginTop: "4px" }}>{metadata.report_generated ? new Date(metadata.report_generated).toLocaleString() : t("notAvailable")}</div>
                  </div>
                  <div style={{ padding: "14px 16px", background: "rgba(59, 130, 246, 0.06)", borderRadius: "10px", border: "1px solid rgba(59, 130, 246, 0.12)" }}>
                    <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#3b82f6", fontWeight: 600 }}>{t("testsAnalyzed")}</div>
                    <div style={{ fontSize: "14px", color: "var(--card-text)", marginTop: "4px" }}>{metadata.total_tests_analyzed ?? t("notAvailable")}</div>
                  </div>
                </div>
                {metadata.test_data_source && (
                  <div style={{ fontSize: "14px", color: "var(--card-text)" }}>
                    <span style={{ fontWeight: 600, color: "var(--muted-text)" }}>{t("dataSource")}</span> {metadata.test_data_source}
                  </div>
                )}
                {metadata.attack_types_covered?.length > 0 && (
                  <div>
                    <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted-text)", marginBottom: "8px", fontWeight: 600 }}>{t("attackTypesCovered")}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {metadata.attack_types_covered.map((a, i) => (
                        <span key={i} style={{ padding: "6px 12px", background: "rgba(59, 130, 246, 0.1)", borderRadius: "8px", fontSize: "13px", color: "var(--card-text)" }}>{getAttackTypeLabel(a)}</span>
                      ))}
                    </div>
                  </div>
                )}
                {metadata.config_summary && (
                  <div style={{ padding: "14px 16px", background: "var(--card-bg)", borderRadius: "10px", border: "1px solid var(--card-border)" }}>
                    <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted-text)", marginBottom: "8px", fontWeight: 600 }}>{t("configSummary")}</div>
                    <div style={{ fontSize: "14px", color: "var(--card-text)" }}>
                      {t("totalTestsLabel")}: {metadata.config_summary.total_tests ?? t("notAvailable")} · {t("overallRisk")}: {metadata.config_summary.overall_risk_level != null ? (levelDisplayNames[metadata.config_summary.overall_risk_level.toLowerCase()] || metadata.config_summary.overall_risk_level) : t("notAvailable")}
                    </div>
                  </div>
                )}
              </div>
            </SectionCardAccordion>
          )}
        </div>
      )}
    </div>
  );
};

export default AgenticAIDosReportTabRecommendations;
